import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const INTERESTS = [
  "Youth Education",
  "Housing & Infrastructure",
  "Women's Livelihoods",
  "Civic Engagement",
  "Neighborhood Action",
  "Wherever I'm needed",
];

const AVAILABILITY = ["Weekends", "Evenings", "Weekdays", "Full-time", "Remote"];

export default function Volunteer({ api }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
    interest: INTERESTS[0],
    availability: AVAILABILITY[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.city) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${api}/volunteers`, form);
      toast.success("You're in.", {
        description: "We'll be in touch within 5 working days.",
      });
      setForm({
        full_name: "",
        email: "",
        phone: "",
        city: "",
        interest: INTERESTS[0],
        availability: AVAILABILITY[0],
        message: "",
      });
    } catch (err) {
      toast.error("Something didn't go through. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="volunteer"
      data-testid="volunteer-section"
      className="relative py-20 md:py-32 bg-sand"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* Left intro */}
          <div className="md:col-span-5">
            <p className="overline mb-6">Get involved</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-ink leading-tight text-balance">
              Bring your <em className="italic text-terracotta">hands</em>, your skills, or just your saturday.
            </h2>
            <p className="mt-6 text-graphite md:text-lg leading-relaxed">
              Every project we run is volunteer-shaped. Tell us a bit about you
              and where you'd like to plug in.
            </p>

            <div className="mt-10 aspect-[4/3] overflow-hidden hidden md:block">
              <img
                src="/imgs/indian-volunteers-action.jpg"
                alt="Volunteers in action"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right form */}
          <div className="md:col-span-7">
            <form
              onSubmit={submit}
              data-testid="volunteer-form"
              className="bg-cream border border-line p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <Field label="Full name *" required>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={update("full_name")}
                  required
                  data-testid="volunteer-input-name"
                  className="vfield"
                />
              </Field>
              <Field label="Email *" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  data-testid="volunteer-input-email"
                  className="vfield"
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="volunteer-input-phone"
                  className="vfield"
                />
              </Field>
              <Field label="City *" required>
                <input
                  type="text"
                  value={form.city}
                  onChange={update("city")}
                  required
                  data-testid="volunteer-input-city"
                  className="vfield"
                />
              </Field>
              <Field label="I'm interested in">
                <select
                  value={form.interest}
                  onChange={update("interest")}
                  data-testid="volunteer-select-interest"
                  className="vfield"
                >
                  {INTERESTS.map((i) => (
                    <option key={i}>{i}</option>
                  ))}
                </select>
              </Field>
              <Field label="Availability">
                <select
                  value={form.availability}
                  onChange={update("availability")}
                  data-testid="volunteer-select-availability"
                  className="vfield"
                >
                  {AVAILABILITY.map((i) => (
                    <option key={i}>{i}</option>
                  ))}
                </select>
              </Field>
              <Field label="Anything else" full>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="volunteer-input-message"
                  className="vfield resize-none"
                  placeholder="Skills, languages spoken, prior experience..."
                />
              </Field>

              <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-graphite">
                  By submitting, you agree to be contacted by our team.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="volunteer-submit"
                  className="inline-flex items-center gap-2 bg-forest hover:bg-terracotta text-cream hover:text-white px-7 py-3.5 transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Sign me up
                      <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .vfield {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #E5E2D9;
          padding: 12px 14px;
          font-family: 'Work Sans', sans-serif;
          font-size: 15px;
          color: #1A1C1A;
          border-radius: 0;
          transition: border-color 0.2s;
        }
        .vfield:focus {
          outline: none;
          border-color: #C85A40;
          box-shadow: 0 0 0 3px rgba(200,90,64,0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children, full = false, required = false }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="overline">
        {label}
        {required && <span className="text-terracotta ml-1">•</span>}
      </span>
      {children}
    </label>
  );
}
